import { useDeliveryRunDetail } from '~/features/deliveries/hooks/use-delivery-run-detail';
import { useProductLookup } from '~/features/deliveries/hooks/use-product-lookup';
import type { ProductLookupRow } from '~/features/deliveries/types/delivery-runs.types';

import { useRunProgress } from './use-run-progress';

/**
 * One product's delivery outcome for one batch — every destination it fanned out to, by its
 * upstream curation id (the listing's `feedProductId`, `idType: 'source'`; a source id can match
 * several delivery rows, so every row is returned, never just the first). Mirrors track W4's
 * shared `useRunProductDeliveryOutcome({ runId, feedProductId })` (not yet on this branch) so the
 * two can be deduped at merge.
 *
 * Resolution: `RunProgress.delivery.externalJobRef` (the catalog run's delivery push) IS the
 * deliveries feature's own run id (confirmed by `useDeliveryRunProducts` taking the same value as
 * its `runId`) — so `useDeliveryRunDetail(externalJobRef)` gives the `connectionId`
 * `lookupProducts` needs; there's no run-scoped images/health-style endpoint for deliveries.
 */
export function useJourneyDeliveryOutcome(
  { runId, feedProductId }: { runId: string; feedProductId: string | null },
  enabled = true,
) {
  const progress = useRunProgress(enabled ? runId : '');
  const externalJobRef = progress.data?.delivery?.externalJobRef ?? null;

  // The run record itself names the connection id — never polled here, this is a one-off lookup.
  const deliveryRun = useDeliveryRunDetail(externalJobRef ?? '', enabled && !!externalJobRef, {
    poll: false,
  });
  const connectionId = deliveryRun.data?.connectionId ?? null;

  const lookup = useProductLookup(
    connectionId ?? '',
    { ids: feedProductId ? [feedProductId] : [], idType: 'source', deliveryJobId: externalJobRef },
    enabled && !!connectionId && !!feedProductId,
  );

  if (!enabled) return { status: 'idle' as const, rows: [] as ProductLookupRow[] };
  if (progress.isLoading || (externalJobRef && deliveryRun.isLoading)) {
    return { status: 'loading' as const, rows: [] as ProductLookupRow[] };
  }
  if (!externalJobRef) return { status: 'no-job' as const, rows: [] as ProductLookupRow[] };
  if (deliveryRun.isError) return { status: 'error' as const, rows: [] as ProductLookupRow[] };
  if (!connectionId) return { status: 'no-connection' as const, rows: [] as ProductLookupRow[] };
  if (lookup.isLoading) return { status: 'loading' as const, rows: [] as ProductLookupRow[] };
  if (lookup.isError) return { status: 'error' as const, rows: [] as ProductLookupRow[] };
  const rows = lookup.data?.products ?? [];
  if (rows.length === 0) return { status: 'not-found' as const, rows: [] as ProductLookupRow[] };
  return { status: 'found' as const, rows };
}
